// src/components/black-friday/missing-account-banner.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { useBFAccounts } from "@/contexts/black-friday-account-context";

/**
 * Banner that shows when user was working on a main app account
 * that is NOT configured for Black Friday tracking
 */
export function MissingAccountBanner() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { accounts: bfAccounts } = useBFAccounts();
  const [missingAccount, setMissingAccount] = useState<any>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isSignedIn || !user || dismissed) return;

    // Get main app accounts from Clerk
    const mainAccounts = user.unsafeMetadata?.accounts as any[] | undefined;
    if (!mainAccounts || mainAccounts.length === 0) return;

    // Get the current active account from main app
    const activeAccountId = user.unsafeMetadata?.activeDexieId as string | undefined;
    if (!activeAccountId) return;

    const currentAccount = mainAccounts.find((acc: any) => acc.dexieId === activeAccountId);
    if (!currentAccount) return;

    // Check if this account is already in BF accounts
    const accountCID = currentAccount.accountCID || currentAccount.customerId;
    if (!accountCID) return;

    const isInBFAccounts = bfAccounts.some(
      (bfAcc) => bfAcc.accountCID === accountCID
    );

    if (!isInBFAccounts) {
      setMissingAccount({
        name: currentAccount.name,
        cid: accountCID,
        dexieId: currentAccount.dexieId,
      });
    }
  }, [isSignedIn, user, bfAccounts, dismissed]);

  if (!missingAccount || dismissed) return null;

  return (
    <Alert className="mb-4 bg-yellow-50 border-yellow-200">
      <div className="flex items-start justify-between w-full">
        <div className="flex-1 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <AlertDescription className="text-yellow-900">
              <strong>You were just working on "{missingAccount.name}" (CID: {missingAccount.cid})</strong> in the main app.
              <br />
              This account is not configured for Black Friday tracking yet.
            </AlertDescription>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                onClick={() => navigate("/bf/settings")}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                Add This Account to BF Tracking
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDismissed(true)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDismissed(true)}
          className="ml-2"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </Alert>
  );
}
